
// Enhanced performance optimization utilities for faster app loading

export class PerformanceOptimizer {
  static DEBUG_PERF = String((import.meta as any).env?.VITE_DEBUG_PERF || '').toLowerCase() === 'true'
  
  // Initialize early performance observers (must run ASAP for accurate metrics)
  static initializeEarlyObservers() {
    if (!('PerformanceObserver' in window)) return
    
    // LCP Observer with buffering to capture early entries
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.log('🎯 LCP:', Math.round(entry.startTime) + 'ms', entry)
      }
    }).observe({ type: 'largest-contentful-paint', buffered: true })
    
    // FCP Observer
    try {
      const supported = (PerformanceObserver as any).supportedEntryTypes || []
      if (Array.isArray(supported) && supported.includes('first-contentful-paint')) {
        new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            console.log('🚀 FCP:', Math.round(entry.startTime) + 'ms')
          }
        }).observe({ type: 'first-contentful-paint', buffered: true })
      } else {
        // Optional: avoid noisy console errors on unsupported browsers
        // console.debug('FCP entry type not supported in this browser')
      }
    } catch {
      // Swallow errors for unsupported observer types
    }
  }
  
  // Preconnect to critical origins only (no wasteful preloads)
  static preconnectCriticalOrigins() {
    const criticalOrigins = [
      // Supabase API
      import.meta.env.VITE_SUPABASE_URL || 'https://yaepfwttuetyalzmvzmj.supabase.co',
      // S3/MinIO for images
      'https://s3.calone.com',
      // Clerk auth
      'https://accounts.calone.com',
    ]
    
    criticalOrigins.forEach(href => {
      const link = document.createElement('link')
      link.rel = 'preconnect'
      link.href = href
      link.crossOrigin = 'anonymous'
      document.head.appendChild(link)
    })
  }
  
  // Prefetch only the next likely pages (not everything)
  static prefetchLikelyPages() {
    // Only prefetch after user interaction to avoid wasting bandwidth
    const prefetchAfterInteraction = () => {
      const likelyPages = [
        '/dashboard',
        '/explore',
      ]
      
      likelyPages.forEach(href => {
        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = href
        document.head.appendChild(link)
      })
    }
    
    // Wait for first user interaction
    const events = ['mousedown', 'touchstart', 'keydown'] as const
    const handleInteraction = () => {
      prefetchAfterInteraction()
      events.forEach(event => 
        document.removeEventListener(event, handleInteraction, { passive: true } as EventListenerOptions)
      )
    }
    
    events.forEach(event => 
      document.addEventListener(event, handleInteraction, { passive: true } as EventListenerOptions)
    )
  }
  
  // Optimize critical resource loading
  static optimizeCriticalResources() {
    // Vite injects CSS; avoid manual preloads that 404 in production.
    // Intentionally no-op to prevent unused preload warnings.
  }
  
  // Initialize performance optimizations
  static initialize() {
    // Initialize early observers immediately
    this.initializeEarlyObservers()
    
    // Run critical optimizations ASAP
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.preconnectCriticalOrigins()
        this.optimizeCriticalResources()
        
        // Defer non-critical prefetching
        setTimeout(() => {
          this.prefetchLikelyPages()
        }, 2000)
      })
    } else {
      this.preconnectCriticalOrigins()
      this.optimizeCriticalResources()
      setTimeout(() => {
        this.prefetchLikelyPages()
      }, 1000)
    }
  }
  
  // Enhanced performance reporting with resource analysis
  static reportPerformanceMetrics() {
    if (!('performance' in window)) return
    
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    
    // Basic navigation metrics
    const metrics = {
      domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
      loadComplete: Math.round(navigation.loadEventEnd - navigation.fetchStart),
      firstContentfulPaint: 0,
      largestContentfulPaint: 0,
    }
    
    // Get paint metrics
    const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0]
    if (fcpEntry) metrics.firstContentfulPaint = Math.round(fcpEntry.startTime)
    
    const lcpEntry = performance.getEntriesByName('largest-contentful-paint')[0]  
    if (lcpEntry) metrics.largestContentfulPaint = Math.round(lcpEntry.startTime)
    
    console.log('🚀 Performance Metrics:', metrics)
    
    // Analyze resource loading patterns
    this.analyzeResourceUsage()
  }
  
  // Analyze unused preloads and slow resources
  static analyzeResourceUsage() {
    const preloads = [...document.querySelectorAll('link[rel=preload]')].map(l => (l as HTMLLinkElement).href)
    const resources = performance.getEntriesByType('resource').map(e => e.name)
    const unusedPreloads = preloads.filter(href => !resources.find(url => url.startsWith(href)))
    
    if (unusedPreloads.length > 0) {
      console.warn('🔥 Unused preloads detected:', unusedPreloads)
    }
    
    // Analyze slowest resources
    const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    const slowResources = resourceEntries
      .filter(e => e.duration > 200) // > 200ms
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5)
    
    if (slowResources.length > 0) {
      console.log('🐌 Slowest resources:', slowResources.map(e => ({
        url: e.name,
        duration: Math.round(e.duration) + 'ms'
      })))
    }
  }
  
  // Utility to check for duplicate network calls
  static detectDuplicateCalls() {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
    const urlCounts = resources.reduce((acc, entry) => {
      const url = entry.name
      acc[url] = (acc[url] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    const duplicates = Object.entries(urlCounts)
      .filter(([_, count]) => count > 1)
      .map(([url, count]) => ({ url, count }))
    
    if (duplicates.length > 0) {
      if (this.DEBUG_PERF && (import.meta as any).env?.DEV) {
        console.warn('🔄 Duplicate network calls detected:', duplicates)
      }
    }

    return duplicates
  }
}

// Auto-initialize when module loads
PerformanceOptimizer.initialize()

// Report metrics after page load with duplicate detection
window.addEventListener('load', () => {
  setTimeout(() => {
    PerformanceOptimizer.reportPerformanceMetrics()
    if (PerformanceOptimizer.DEBUG_PERF) {
      PerformanceOptimizer.detectDuplicateCalls()
    }
  }, 1000)
})
